import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { AuthedDirective, UnauthedDirective } from '@directives';
import { environment } from '@environments/environment';
import { DebugService } from '@services';
import { DesignatedApproverDirective } from '@shared/directives/designated-approver.directive';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { BillingDialogComponent } from 'src/app/billing/components/billing-dialog/billing-dialog.component';
import { GCAuthenticationService } from 'src/app/billing/shared/service/gc-authentication.service';
import { ChatDialogService } from 'src/app/chat/service';
import { ChildPageRoute, GknRouteLabel, GknRoutePath } from '../../app-routing.enum';
import { AppHeaderProductSelectComponent } from '../app-header-product-select/app-header-product-select.component';
import { AppHeaderSearchBarComponent } from '../app-header-search-bar/app-header-search-bar.component';
import { AppHeaderComponent } from '../app-header.component';
import { AppMegaMenuComponent } from '../app-mega-menu/app-mega-menu.component';

@Component({
    selector: 'app-header-desktop',
    standalone: true,
    imports: [
        CommonModule,
        AppHeaderProductSelectComponent,
        AppHeaderSearchBarComponent,
        AuthedDirective,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatSlideToggleModule,
        MatTooltipModule,
        RouterModule,
        UnauthedDirective,
        AppMegaMenuComponent,
        DesignatedApproverDirective,
    ],
    templateUrl: './app-header-desktop.component.html',
    styleUrls: ['./app-header-desktop.component.scss'],
})
export class AppHeaderDesktopComponent implements OnInit {
    parent = inject(AppHeaderComponent);
    private bo = inject(BreakpointObserver);
    private dialog = inject(MatDialog);
    private chatDialogService = inject(ChatDialogService);
    debugService = inject(DebugService);
    private router = inject(Router);
    private gcAuthService = inject(GCAuthenticationService);

    GknRoutePath = GknRoutePath;
    ChildPageRoute = ChildPageRoute;
    GknRouteLabel = GknRouteLabel;
    environment = environment;

    @Input() breakpointClass: string = 'desktop';

    homeRoutePath = '/' + GknRoutePath.HOME;
    profileRoutePath = '/' + GknRoutePath.PROFILE;
    @ViewChild(MatMenuTrigger) userMenuTrigger: MatMenuTrigger;

    // Desktop-specific properties
    activeMegaMenu: string | null = null;
    showCustomerAssistant$ = new BehaviorSubject(false);

    private defaultScrollThreshold = 400;
    private homeScrollThreshold = 1000;
    /** for enum visibility in html template */
    orchestratorsRouteLabel = GknRouteLabel;

    readonly AMA_TITLE = environment.customerAssistant.label;

    @HostListener('window:scroll', [])
    onWindowScroll() {
        const header = document.querySelector('header');
        if (header) {
            const threshold =
                this.parent.activePage === `/${GknRoutePath.HOME}`
                    ? this.homeScrollThreshold
                    : this.defaultScrollThreshold;
            if (window.scrollY > threshold) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }
        }
    }

    ngOnInit(): void {
        this.showCustomerAssistant$.next(environment.featureFlags.digitalAssistant.enabled);
    }

    /**
     * Show Mega Menu by menu Id
     * @param menuId - string
     */
    showMegaMenu(menuId: string): void {
        this.activeMegaMenu = menuId;
    }

    /**
     * Hide Mega Menu
     */
    hideMegaMenu(): void {
        this.activeMegaMenu = null;
    }

    // Desktop-specific dialog method
    openAMA(): void {
        this.closePendoResourceCenter();
        this.chatDialogService.open();
    }

    openBillingDialog(): void {
        firstValueFrom(this.userMenuTrigger.menuClosed).then(() => {
            if (
                this.gcAuthService.getPKCETokenFromSession() ||
                this.router.url.startsWith(`/${GknRoutePath.BILLING}`)
            ) {
                this.router.navigate([`/${GknRoutePath.BILLING}`], { queryParams: { login: true } });
            } else {
                this.dialog.open(BillingDialogComponent, {
                    width: '600px',
                    panelClass: 'dialog-rounded',
                });
            }
        });
    }

    /**
     * Dismisses the Pendo Resource Center dropdown if it is currently open.
     * Used to prevent overlap with other UI elements like the profile menu and AI dialog.
     */
    closePendoResourceCenter(): void {
        try {
            (window as any).pendo?.BuildingBlocks?.BuildingBlockResourceCenter?.dismissResourceCenter();
        } catch (_) {
            // Pendo may not be loaded
        }
    }
}
